import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import AutoTextarea from "./AutoTextarea";

/**
 * AiInlineField — campo testo libero con pulsante "Suggerisci con IA" accanto al label.
 * Chiama /api/ai/clinical-suggest e applica il campo richiesto (diagnosi / terapia / protocollo).
 *
 * Props:
 *  - kind: "diagnosi" | "terapia" | "protocollo"
 *  - label: titolo del campo
 *  - value, onChange, readOnly
 *  - context: {anamnesi, OD, OS, diagnosi, terapia}
 *  - testid: override testid (default: "field-{kind}")
 *  - rows: minimo rows
 */
export default function AiInlineField({ kind, label, value, onChange, readOnly, context, testid, rows = 4 }) {
  const [busy, setBusy] = useState(false);
  const askAi = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/clinical-suggest", {
        anamnesi: context?.anamnesi || "",
        OD: context?.OD || {},
        OS: context?.OS || {},
        diagnosi_attuale: context?.diagnosi || "",
        free_text: kind === "terapia" ? `Diagnosi attuale: ${context?.diagnosi || "—"}` : (kind === "protocollo" ? `Diagnosi: ${context?.diagnosi || "—"} · Terapia: ${context?.terapia || "—"}` : ""),
      });
      let appended = "";
      if (kind === "diagnosi") {
        const top = (data.differential || [])[0];
        appended = top ? `${top.label}${top.probability ? ` (${Math.round((top.probability || 0) * 100)}%)` : ""}${top.rationale ? ` — ${top.rationale}` : ""}` : (data.diagnosis_summary || "");
      } else if (kind === "terapia") {
        const lines = (data.therapy_suggestion || []).map((t) => `${t.drug || t.label || ""} ${t.posology || ""}`.trim()).filter(Boolean);
        appended = lines.join("\n") || data.therapy_text || "";
      } else if (kind === "protocollo") {
        appended = data.protocol || data.behavioral_protocol || (data.guidelines || []).join("\n");
      }
      if (!appended) {
        toast.info("L'IA non ha prodotto un suggerimento. Riprova fornendo più contesto.");
        return;
      }
      onChange?.(value ? `${value}\n\n${appended}` : appended);
      toast.success(`${label}: suggerimento IA applicato`);
      if (data.demo_fallback) toast.info("IA in modalità demo (fallback coerente)");
    } catch (e) {
      toast.error(apiErr(e, "Errore IA"));
    } finally {
      setBusy(false);
    }
  };

  const accentColor = kind === "diagnosi" ? "text-brand-navy" : kind === "terapia" ? "text-brand-azure" : "text-brand-orange";

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-5" data-testid={`${testid || `field-${kind}`}-block`}>
      <div className="flex items-center justify-between mb-2">
        <label className={`text-xs font-bold uppercase tracking-wider ${accentColor}`}>{label}</label>
        {!readOnly && (
          <button
            type="button"
            disabled={busy}
            onClick={askAi}
            data-testid={`ai-suggest-${kind}`}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-brand-azure hover:text-brand-navy bg-brand-azure/10 hover:bg-brand-azure/20 px-2.5 py-1.5 rounded-md transition disabled:opacity-60"
            title={`Suggerisci ${label.toLowerCase()} con IA`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Suggerisci con IA
          </button>
        )}
      </div>
      <AutoTextarea
        value={value}
        onChange={onChange}
        disabled={readOnly}
        rows={rows}
        testid={testid || `field-${kind}`}
        placeholder={kind === "diagnosi" ? "Inserisci la diagnosi clinica…" : kind === "terapia" ? "Inserisci la terapia (farmaci, posologia, durata)…" : "Inserisci il protocollo / piano di follow-up…"}
      />
    </div>
  );
}
